import { useState } from 'react';
import { Field } from './Field';
import { Button } from '../common/Button';
import { Icon } from '../common/Icon';
import { Spinner } from '../common/Spinner';
import { useProducts } from '../../hooks';
import { submitEnquiry, EnquiryValidationError, type EnquiryType, type EnquiryResult } from '../../services/enquiryService';

interface EnquiryFormProps {
  defaultType?: EnquiryType;
  lockType?: boolean;
  defaultProductSlug?: string;
}

const TYPE_OPTIONS: { value: EnquiryType; label: string }[] = [
  { value: 'general', label: 'General enquiry' },
  { value: 'product', label: 'Product enquiry' },
  { value: 'farmer', label: 'Farmer enquiry' },
  { value: 'retailer', label: 'Become a retailer' },
  { value: 'distributor', label: 'Become a distributor' },
];

export function EnquiryForm({ defaultType = 'general', lockType = false, defaultProductSlug = '' }: EnquiryFormProps) {
  const { data: products = [] } = useProducts();
  const [type, setType] = useState<EnquiryType>(defaultType);
  const [form, setForm] = useState({
    name: '',
    phone: '',
    email: '',
    location: '',
    productSlug: defaultProductSlug,
    message: '',
    company: '', // honeypot
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [status, setStatus] = useState<'idle' | 'submitting' | 'error'>('idle');
  const [result, setResult] = useState<EnquiryResult | null>(null);

  const set = (key: keyof typeof form) => (v: string) => setForm((f) => ({ ...f, [key]: v }));

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setStatus('submitting');
    setErrors({});
    try {
      const res = await submitEnquiry({ type, ...form });
      setResult(res);
      setStatus('idle');
    } catch (err) {
      if (err instanceof EnquiryValidationError) {
        setErrors(err.fields);
        setStatus('idle');
      } else {
        setStatus('error');
      }
    }
  }

  if (result) {
    return (
      <div className="flex flex-col items-center rounded-2xl border border-brand-200 bg-brand-50/60 p-10 text-center" role="status" aria-live="polite">
        <span className="flex h-16 w-16 items-center justify-center rounded-full bg-brand-500 text-white">
          <Icon name="Check" className="h-8 w-8" />
        </span>
        <h3 className="mt-5 font-display text-2xl font-semibold text-brand-900">Enquiry received</h3>
        <p className="mt-2 max-w-sm text-ink-600">
          Thanks, {form.name.split(' ')[0] || 'there'} — we’ll be in touch soon. Your reference is{' '}
          <span className="font-semibold text-brand-800">{result.reference}</span>.
        </p>
        <Button
          variant="outline"
          size="md"
          className="mt-6"
          onClick={() => {
            setResult(null);
            setForm({ name: '', phone: '', email: '', location: '', productSlug: '', message: '', company: '' });
          }}
        >
          Send another enquiry
        </Button>
      </div>
    );
  }

  return (
    <form onSubmit={onSubmit} noValidate className="space-y-5">
      {!lockType && (
        <Field as="select" id="type" label="I’m enquiring about" value={type} onChange={(v) => setType(v as EnquiryType)} options={TYPE_OPTIONS} />
      )}

      <div className="grid gap-5 sm:grid-cols-2">
        <Field id="name" label="Full name" value={form.name} onChange={set('name')} error={errors.name} required autoComplete="name" />
        <Field id="phone" label="Phone" type="tel" value={form.phone} onChange={set('phone')} error={errors.phone} required autoComplete="tel" />
        <Field id="email" label="Email" type="email" value={form.email} onChange={set('email')} error={errors.email} autoComplete="email" hint="Optional" />
        <Field id="location" label="Location / District" value={form.location} onChange={set('location')} hint="Optional" />
      </div>

      {type === 'product' && (
        <Field
          as="select"
          id="product"
          label="Product"
          value={form.productSlug}
          onChange={set('productSlug')}
          options={[{ value: '', label: 'Not specific / multiple' }, ...products.map((p) => ({ value: p.slug, label: p.name }))]}
        />
      )}

      <Field
        as="textarea"
        id="message"
        label="Your message"
        value={form.message}
        onChange={set('message')}
        error={errors.message}
        required
        placeholder="Tell us about your crop, your question, or what you’re looking for…"
      />

      {/* honeypot (hidden from users) */}
      <div className="absolute left-[-9999px]" aria-hidden="true">
        <label htmlFor="company">Company (leave blank)</label>
        <input id="company" tabIndex={-1} autoComplete="off" value={form.company} onChange={(e) => set('company')(e.target.value)} />
      </div>

      {status === 'error' && (
        <p role="alert" className="flex items-center gap-2 rounded-lg bg-red-50 p-3 text-sm text-red-700">
          <Icon name="AlertCircle" className="h-4 w-4" />
          Something went wrong sending your enquiry. Please try again or call us directly.
        </p>
      )}

      <div className="flex items-center gap-4">
        <Button variant="primary" size="lg" type="submit" disabled={status === 'submitting'}>
          {status === 'submitting' ? (
            <>
              <Spinner className="h-5 w-5" /> Sending…
            </>
          ) : (
            <>
              Send enquiry <Icon name="Send" className="h-5 w-5" />
            </>
          )}
        </Button>
        <p className="text-xs text-ink-500">We’ll only use your details to respond to this enquiry.</p>
      </div>
    </form>
  );
}
